import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Building2, ChevronDown, ChevronRight, Check, Search, LayoutGrid } from 'lucide-react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const COMBINED_VALUE = '__combined__'
// Below this count a search box just adds noise; above it, it earns its keep
// for owners running a large multi-location chain.
const SEARCH_THRESHOLD = 6

export default function RestaurantSwitcher() {
  const {
    restaurants,
    currentRestaurant,
    isLoading,
    selectRestaurant,
    selectCombinedView,
    viewMode,
    hasMultipleRestaurants
  } = useRestaurant()

  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    // Small tolerance so sub-pixel rounding doesn't leave a stuck arrow.
    setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 4)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState, { passive: true })
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      resizeObserver.disconnect()
    }
  }, [updateScrollState, restaurants.length])

  const scrollTabsRight = () => {
    scrollRef.current?.scrollBy({ left: 240, behavior: 'smooth' })
  }

  // Must run unconditionally (before any early return) to keep hook order
  // stable across renders — restaurants can grow from 1 to 2+ post-login,
  // which would otherwise flip which branch below returns early.
  const filteredRestaurants = useMemo(() => {
    if (!searchTerm.trim()) return restaurants
    const q = searchTerm.trim().toLowerCase()
    return restaurants.filter(r =>
      r.name.toLowerCase().includes(q) || r.address?.city?.toLowerCase().includes(q)
    )
  }, [restaurants, searchTerm])

  // Don't show switcher if user has no restaurants
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-10 w-48" />
      </div>
    )
  }

  if (!currentRestaurant || restaurants.length === 0) {
    return null
  }

  // Single restaurant — unchanged plain display, exactly as before. This branch
  // must stay untouched so existing single-restaurant owners see zero change.
  if (!hasMultipleRestaurants) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg dark:bg-black dark:bg-[linear-gradient(90deg,rgba(0,0,0,1)_0%,rgb(65_37_30)_70%,rgba(36,21,14,1)_100%)]">
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <div className="flex flex-col min-w-0">
          <span className="font-semibold text-sm truncate">
            {currentRestaurant.name}
          </span>
          <span className="text-xs text-muted-foreground truncate hidden sm:block">
            {currentRestaurant.address?.city || 'Restaurant'}
          </span>
        </div>
      </div>
    )
  }

  // Head of Restaurant (multiple assigned restaurants).
  const isCombined = viewMode === 'combined'
  const triggerLabel = isCombined ? 'All Restaurants' : (currentRestaurant?.name || 'Select Restaurant')
  const triggerSubtitle = isCombined ? `${restaurants.length} restaurants` : (currentRestaurant?.address?.city || 'Restaurant')
  const activeTabValue = isCombined ? COMBINED_VALUE : (currentRestaurant?._id || '')

  const handleSelect = (value: string) => {
    if (value === COMBINED_VALUE) {
      selectCombinedView()
    } else {
      selectRestaurant(value)
    }
    setIsOpen(false)
    setSearchTerm('')
  }

  return (
    <>
      {/* Mobile — a single compact trigger that opens a searchable, scrollable
          picker sheet. Scales cleanly to owners with 10+ restaurants. */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="md:hidden flex items-center gap-2 px-3 py-2 bg-muted/50 hover:bg-muted rounded-lg dark:bg-black dark:bg-[linear-gradient(90deg,rgba(0,0,0,1)_0%,rgb(65_37_30)_70%,rgba(36,21,14,1)_100%)] transition-colors max-w-full min-w-0"
        >
          {isCombined ? (
            <LayoutGrid className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          ) : (
            <Building2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          )}
          <div className="flex flex-col min-w-0 text-left">
            <span className="font-semibold text-sm truncate max-w-[10rem] sm:max-w-[16rem]">
              {triggerLabel}
            </span>
            <span className="text-xs text-muted-foreground truncate hidden sm:block">
              {triggerSubtitle}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </button>

        <SheetContent side="bottom" className="max-w-lg mx-auto rounded-t-xl">
          <SheetHeader>
            <SheetTitle>Switch Restaurant</SheetTitle>
          </SheetHeader>

          {restaurants.length > SEARCH_THRESHOLD && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search restaurants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          )}

          <div className="overflow-y-auto -mx-1 px-1 space-y-1">
            {!searchTerm && (
              <button
                type="button"
                onClick={() => handleSelect(COMBINED_VALUE)}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-muted transition-colors"
              >
                <LayoutGrid className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">All Restaurants</div>
                  <div className="text-xs text-muted-foreground">Combined view across {restaurants.length} restaurants</div>
                </div>
                {isCombined && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
              </button>
            )}

            {filteredRestaurants.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No restaurants match your search</p>
            ) : (
              filteredRestaurants.map((restaurant) => {
                const isActive = !isCombined && currentRestaurant?._id === restaurant._id
                return (
                  <button
                    key={restaurant._id}
                    type="button"
                    onClick={() => handleSelect(restaurant._id)}
                    className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-muted transition-colors"
                  >
                    <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{restaurant.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{restaurant.address?.city || 'Restaurant'}</div>
                    </div>
                    {isActive && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                  </button>
                )
              })
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop — a real tab strip. When it overflows, a slider arrow fades
          in at the right edge; it hides itself once there's nothing left to
          scroll to. */}
      <div className="hidden md:flex relative min-w-0 max-w-full">
        <Tabs value={activeTabValue} onValueChange={handleSelect} className="min-w-0 max-w-full">
          <TabsList
            ref={scrollRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth flex-nowrap min-w-0 max-w-full justify-start"
          >
            <TabsTrigger value={COMBINED_VALUE} className="flex-shrink-0">
              All Restaurants
            </TabsTrigger>
            {restaurants.map((restaurant) => (
              <TabsTrigger
                key={restaurant._id}
                value={restaurant._id}
                className="flex-shrink-0 max-w-[10rem] truncate"
              >
                {restaurant.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {canScrollRight && (
          <button
            type="button"
            onClick={scrollTabsRight}
            aria-label="Show more restaurants"
            className={cn(
              'absolute right-0 top-0 bottom-0 flex items-center justify-center w-8',
              'bg-gradient-to-l from-card via-card/90 to-transparent',
              'text-muted-foreground hover:text-foreground transition-colors'
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </>
  )
}
